import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Edit, Trash2, Phone, Mail } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactpersonenTab = ({ locatieId, locatieData, onUpdate }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingItem(null);
    setShowModal(true);
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Weet je zeker dat je deze contactpersoon wilt verwijderen?')) return;

    try {
      await axios.delete(`${API}/locaties/${locatieId}/contactpersonen/${itemId}`);
      toast.success('Contactpersoon verwijderd');
      onUpdate();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Fout bij verwijderen');
    }
  };

  const contactpersonen = locatieData?.contactpersonen || [];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Contactpersonen</h3>
          <p className="text-sm text-slate-500">{contactpersonen.length} contacten</p>
        </div>
        <Button onClick={handleAdd}>
          <Plus className="w-4 h-4 mr-2" />
          Contact toevoegen
        </Button>
      </div>

      {contactpersonen.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-lg">
          <p className="text-slate-500">Geen contactpersonen toegevoegd</p>
          <Button className="mt-4" onClick={handleAdd}>
            Eerste contact toevoegen
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Naam</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead>Telefoon</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="text-right">Acties</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contactpersonen.map((item) => (
                <TableRow key={item._id}>
                  <TableCell className="font-medium">{item.naam}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-indigo-100 text-indigo-700">
                      {item.rol}
                    </span>
                  </TableCell>
                  <TableCell>
                    {item.telefoon ? (
                      <a href={`tel:${item.telefoon}`} className="flex items-center gap-1 text-blue-600 hover:underline">
                        <Phone className="w-3 h-3" />
                        {item.telefoon}
                      </a>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {item.email ? (
                      <a href={`mailto:${item.email}`} className="flex items-center gap-1 text-blue-600 hover:underline">
                        <Mail className="w-3 h-3" />
                        {item.email}
                      </a>
                    ) : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(item)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(item._id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ContactModal
        open={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingItem(null);
        }}
        item={editingItem}
        locatieId={locatieId}
        onUpdate={onUpdate}
      />
    </div>
  );
};

const ContactModal = ({ open, onClose, item, locatieId, onUpdate }) => {
  const [formData, setFormData] = useState({
    rol: '',
    naam: '',
    telefoon: '',
    email: '',
    notities: ''
  });

  React.useEffect(() => {
    if (item) {
      setFormData({
        rol: item.rol || '',
        naam: item.naam || '',
        telefoon: item.telefoon || '',
        email: item.email || '',
        notities: item.notities || ''
      });
    } else {
      setFormData({
        rol: '',
        naam: '',
        telefoon: '',
        email: '',
        notities: ''
      });
    }
  }, [item, open]);

  const handleSubmit = async () => {
    if (!formData.naam || !formData.rol) {
      toast.error('Vul naam en rol in');
      return;
    }

    try {
      if (item) {
        await axios.put(`${API}/locaties/${locatieId}/contactpersonen/${item._id}`, formData);
        toast.success('Contactpersoon bijgewerkt');
      } else {
        await axios.post(`${API}/locaties/${locatieId}/contactpersonen`, formData);
        toast.success('Contactpersoon toegevoegd');
      }
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Fout bij opslaan');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{item ? 'Contactpersoon bewerken' : 'Contactpersoon toevoegen'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Naam *</Label>
              <Input
                value={formData.naam}
                onChange={(e) => setFormData({ ...formData, naam: e.target.value })}
                placeholder="Piet Jansen"
              />
            </div>
            <div>
              <Label>Rol *</Label>
              <Input
                value={formData.rol}
                onChange={(e) => setFormData({ ...formData, rol: e.target.value })}
                placeholder="Bedrijfsleider"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Telefoon</Label>
              <Input
                value={formData.telefoon}
                onChange={(e) => setFormData({ ...formData, telefoon: e.target.value })}
                placeholder="06-12345678"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="piet@truckwash1.nl"
              />
            </div>
          </div>

          <div>
            <Label>Notities</Label>
            <Textarea
              value={formData.notities}
              onChange={(e) => setFormData({ ...formData, notities: e.target.value })}
              placeholder="Bereikbaar 24/7..."
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuleren</Button>
          <Button onClick={handleSubmit}>{item ? 'Bijwerken' : 'Toevoegen'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ContactpersonenTab;
